'use client';

import '@repo/web-chrome/lib/api-client';
import { ENGLISH_IQ_EVENTS } from '@repo/analytics';
import { trackEvent } from '@repo/web-chrome/lib/analytics';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { EnglishIqMessagesType } from '../english-iq-messages-type';
import { EnglishIqTurnstile } from '../turnstile';
import { EnglishIqV2WizardStage } from './english-iq-v2-wizard-stage';
import { useEnglishIqV2Flow } from './hooks';

interface IEnglishIqV2WizardProps {
  friendResult?: { score: number; subBand: string } | null;
  locale: string;
  messages: EnglishIqMessagesType;
  siteKey: string;
  sourceShareId?: string | null;
}

export function EnglishIqV2Wizard({
  friendResult = null,
  locale,
  messages,
  siteKey,
  sourceShareId = null,
}: IEnglishIqV2WizardProps): React.JSX.Element {
  const { answer, finishDemo, markStartFailed, reset, retryPendingRequest, start, state } =
    useEnglishIqV2Flow({ locale, sourceShareId });
  const [executeSignal, setExecuteSignal] = useState(0);
  const startGate = useEnglishIqV2StartGate(start, markStartFailed, setExecuteSignal);
  useEnglishIqV2EntryTracking(state.hydrated, state.view, sourceShareId);
  useEffect(() => {
    const item = state.assessment.currentItem;
    if (item) {
      trackEvent(ENGLISH_IQ_EVENTS.itemPresented, {
        itemType: item.type,
        skill: item.skill,
        surface: 'web_v2',
      });
    }
  }, [state.assessment.currentItem]);

  return (
    <section className="relative min-h-[100svh] overflow-hidden bg-[#100b08] text-[#fff8e9]">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_15%_10%,rgba(255,122,26,0.14),transparent_32%),radial-gradient(circle_at_90%_90%,rgba(122,49,10,0.2),transparent_38%)]" />
      <div className="relative">
        <EnglishIqV2WizardStage
          answer={answer}
          finishDemo={finishDemo}
          friendResult={friendResult}
          locale={locale}
          messages={messages}
          isStartPending={startGate.isPending}
          onStart={startGate.request}
          reset={reset}
          retryPendingRequest={retryPendingRequest}
          state={state}
        />
      </div>
      <EnglishIqTurnstile
        executeSignal={executeSignal}
        onError={startGate.fail}
        onToken={startGate.receiveToken}
        siteKey={siteKey}
      />
    </section>
  );
}

function useEnglishIqV2EntryTracking(
  hydrated: boolean,
  view: 'start' | 'assessment' | 'demo' | 'result',
  sourceShareId: string | null,
): void {
  const trackedEntryRef = useRef(false);
  const source = resolveEnglishIqV2EntrySource(sourceShareId, view);
  useEffect(() => {
    if (!hydrated || trackedEntryRef.current) {
      return;
    }
    trackedEntryRef.current = true;
    trackEvent(ENGLISH_IQ_EVENTS.entry, {
      source,
      surface: 'web_v2',
    });
    if (source === 'friend_result') {
      trackEvent(ENGLISH_IQ_EVENTS.friendResultViewed, { surface: 'web_v2' });
    }
  }, [hydrated, source]);
}

function resolveEnglishIqV2EntrySource(
  sourceShareId: string | null,
  view: 'start' | 'assessment' | 'demo' | 'result',
): 'friend_result' | 'landing' | 'owned_result' {
  if (!sourceShareId) {
    return 'landing';
  }
  return view === 'start' ? 'friend_result' : 'owned_result';
}

function useEnglishIqV2StartGate(
  start: (turnstileToken?: string) => Promise<void>,
  markStartFailed: () => void,
  setExecuteSignal: React.Dispatch<React.SetStateAction<number>>,
) {
  const pendingRef = useRef(false);
  const tokenInFlightRef = useRef(false);
  const [isPending, setIsPending] = useState(false);
  const request = useCallback(() => {
    if (pendingRef.current) {
      return;
    }
    pendingRef.current = true;
    setIsPending(true);
    setExecuteSignal((value) => value + 1);
  }, [setExecuteSignal]);
  const clear = useCallback(() => {
    pendingRef.current = false;
    setIsPending(false);
  }, []);
  const receiveToken = useCallback(
    async (token?: string) => {
      if (!pendingRef.current || tokenInFlightRef.current) {
        return;
      }
      tokenInFlightRef.current = true;
      await start(token);
      tokenInFlightRef.current = false;
      clear();
    },
    [clear, start],
  );
  const fail = useCallback(() => {
    tokenInFlightRef.current = false;
    markStartFailed();
    clear();
  }, [clear, markStartFailed]);
  return { fail, isPending, receiveToken, request };
}
